import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { RED_NUMBERS, type RuletaBet } from '../utils/ruletaLogic'

interface BettingBoardProps {
  bets: RuletaBet[]
  disabled: boolean
  onNumberClick: (num: number) => void
  onSplitClick: (num1: number, num2: number) => void
  onStreetClick: (row: number) => void
  onCornerClick: (topLeft: number) => void
  onLineClick: (row: number) => void
  onColumnClick: (column: 1 | 2 | 3) => void
  onDozenClick: (dozen: 1 | 2 | 3) => void
  onOutsideClick: (type: 'red' | 'black' | 'even' | 'odd' | 'low' | 'high') => void
}

// Board layout: 3 rows x 12 columns (plus 0)
// Row 0 (top): 3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36
// Row 1 (mid): 2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35
// Row 2 (bot): 1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34
const BOARD_ROWS = [
  [3, 6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36],
  [2, 5, 8, 11, 14, 17, 20, 23, 26, 29, 32, 35],
  [1, 4, 7, 10, 13, 16, 19, 22, 25, 28, 31, 34],
]

// Cell dimensions (small screen values used for positioning)
const CELL_W_SM = 40 // w-10
const CELL_H_SM = 48 // h-12

// Get bet for a position
function getBetForPosition(bets: RuletaBet[], type: string, position: string): RuletaBet | undefined {
  return bets.find((b) => b.type === type && b.position === position)
}

function formatBetAmount(amount: number): string {
  if (amount >= 1000) return `${amount / 1000}K`
  return String(amount)
}

// Chip indicator
function BetChip({ amount }: { amount: number }) {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      className="absolute w-6 h-6 bg-casino-gold rounded-full flex items-center justify-center text-[10px] text-casino-black font-bold shadow-lg border-2 border-yellow-300 z-20"
      style={{ pointerEvents: 'none' }}
    >
      {formatBetAmount(amount)}
    </motion.div>
  )
}

// Number cell component
function NumberCell({
  num,
  bets,
  disabled,
  onClick,
}: {
  num: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const isRed = RED_NUMBERS.includes(num)
  const bet = getBetForPosition(bets, 'straight', `straight-${num}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.05, zIndex: 10 } : undefined}
      whileTap={!disabled ? { scale: 0.95 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative w-10 h-12 md:w-12 md:h-14 flex items-center justify-center',
        'font-bold text-white text-sm md:text-base',
        'border border-casino-gold/30 transition-all',
        isRed ? 'bg-red-700 hover:bg-red-600' : 'bg-gray-900 hover:bg-gray-800',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer'
      )}
    >
      {num}
      {bet && (
        <div className="absolute inset-0 flex items-center justify-center">
          <BetChip amount={bet.amount} />
        </div>
      )}
    </motion.button>
  )
}

// Split bet area (horizontal - between numbers in same row)
function HorizontalSplit({
  num1,
  num2,
  bets,
  disabled,
  onClick,
}: {
  num1: number
  num2: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const [small, large] = num1 < num2 ? [num1, num2] : [num2, num1]
  const bet = getBetForPosition(bets, 'split', `split-${small}-${large}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 12px rgba(212, 175, 55, 0.6)' } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'absolute w-6 h-10 md:w-8 md:h-12 -translate-x-1/2 translate-y-0',
        'bg-casino-gold/10 hover:bg-casino-gold/40 rounded',
        'border border-casino-gold/20 hover:border-casino-gold/60',
        'transition-all cursor-pointer z-10',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      title={`Split ${num1}-${num2}`}
    >
      {bet && <BetChip amount={bet.amount} />}
    </motion.button>
  )
}

// Split bet area (vertical - between numbers in adjacent rows)
function VerticalSplit({
  num1,
  num2,
  bets,
  disabled,
  onClick,
}: {
  num1: number
  num2: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const [small, large] = num1 < num2 ? [num1, num2] : [num2, num1]
  const bet = getBetForPosition(bets, 'split', `split-${small}-${large}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 12px rgba(212, 175, 55, 0.6)' } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'absolute w-10 h-6 md:w-12 md:h-8 translate-x-0 -translate-y-1/2',
        'bg-casino-gold/10 hover:bg-casino-gold/40 rounded',
        'border border-casino-gold/20 hover:border-casino-gold/60',
        'transition-all cursor-pointer z-10',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      title={`Split ${num1}-${num2}`}
    >
      {bet && <BetChip amount={bet.amount} />}
    </motion.button>
  )
}

// Corner bet area (intersection of 4 numbers)
function CornerBet({
  topLeft,
  bets,
  disabled,
  onClick,
}: {
  topLeft: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const bet = getBetForPosition(bets, 'corner', `corner-${topLeft}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.4, zIndex: 50, boxShadow: '0 0 15px rgba(212, 175, 55, 0.8)' } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'absolute w-7 h-7 md:w-8 md:h-8 -translate-x-1/2 -translate-y-1/2',
        'bg-casino-gold/15 hover:bg-casino-gold/50 rounded-full',
        'border-2 border-casino-gold/30 hover:border-casino-gold/80',
        'transition-all cursor-pointer z-20',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      title={`Corner ${topLeft}`}
    >
      {bet && <BetChip amount={bet.amount} />}
    </motion.button>
  )
}

// Street bet area (left edge of row - 3 numbers)
function StreetBet({
  row,
  bets,
  disabled,
  onClick,
}: {
  row: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const bet = getBetForPosition(bets, 'street', `street-${row}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 10px rgba(212, 175, 55, 0.5)' } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'absolute w-5 h-10 md:w-6 md:h-12',
        'bg-casino-gold/10 hover:bg-casino-gold/40 rounded-l',
        'border border-casino-gold/20 hover:border-casino-gold/60',
        'transition-all cursor-pointer z-10',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      title={`Street ${row * 3 - 2}-${row * 3}`}
    >
      {bet && <BetChip amount={bet.amount} />}
    </motion.button>
  )
}

// Line bet area (between two rows - 6 numbers)
function LineBet({
  row,
  bets,
  disabled,
  onClick,
}: {
  row: number
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
}) {
  const bet = getBetForPosition(bets, 'line', `line-${row}`)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 10px rgba(212, 175, 55, 0.5)' } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'absolute w-5 h-6 md:w-6 md:h-8 -translate-y-1/2',
        'bg-casino-gold/10 hover:bg-casino-gold/40 rounded',
        'border border-casino-gold/20 hover:border-casino-gold/60',
        'transition-all cursor-pointer z-15',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      title={`Line ${row * 3 - 2}-${row * 3 + 3}`}
    >
      {bet && <BetChip amount={bet.amount} />}
    </motion.button>
  )
}

// Outside bet cell
function OutsideBetCell({
  label,
  betType,
  bets,
  disabled,
  onClick,
  className,
  color,
}: {
  label: string
  betType: string
  bets: RuletaBet[]
  disabled: boolean
  onClick: () => void
  className?: string
  color?: 'red' | 'black'
}) {
  const bet = bets.find((b) => b.type === betType)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02 } : undefined}
      whileTap={!disabled ? { scale: 0.98 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative flex items-center justify-center font-bold text-white text-xs md:text-sm',
        'border border-casino-gold/30 transition-all',
        color === 'red' && 'bg-red-700 hover:bg-red-600',
        color === 'black' && 'bg-gray-900 hover:bg-gray-800',
        !color && 'bg-casino-black-light hover:bg-casino-black',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        className
      )}
    >
      {label}
      {bet && (
        <div className="absolute -top-1 -right-1 z-20">
          <BetChip amount={bet.amount} />
        </div>
      )}
    </motion.button>
  )
}

export function BettingBoard({
  bets,
  disabled,
  onNumberClick,
  onSplitClick,
  onStreetClick,
  onCornerClick,
  onLineClick,
  onColumnClick,
  onDozenClick,
  onOutsideClick,
}: BettingBoardProps) {
  // Generate all valid split positions
  const generateSplits = () => {
    const splits: { num1: number; num2: number; type: 'h' | 'v'; row: number; col: number }[] = []

    // Horizontal splits (between adjacent numbers in same row)
    for (let row = 0; row < BOARD_ROWS.length; row++) {
      for (let col = 0; col < BOARD_ROWS[row].length - 1; col++) {
        splits.push({
          num1: BOARD_ROWS[row][col],
          num2: BOARD_ROWS[row][col + 1],
          type: 'h',
          row,
          col,
        })
      }
    }

    // Vertical splits (between adjacent numbers in different rows)
    for (let row = 0; row < BOARD_ROWS.length - 1; row++) {
      for (let col = 0; col < BOARD_ROWS[row].length; col++) {
        splits.push({
          num1: BOARD_ROWS[row][col],
          num2: BOARD_ROWS[row + 1][col],
          type: 'v',
          row,
          col,
        })
      }
    }

    return splits
  }

  // Generate corner positions
  const generateCorners = () => {
    const corners: { topLeft: number; row: number; col: number }[] = []

    for (let row = 0; row < BOARD_ROWS.length - 1; row++) {
      for (let col = 0; col < BOARD_ROWS[row].length - 1; col++) {
        // The top-left number of the corner
        const topLeft = Math.min(
          BOARD_ROWS[row][col],
          BOARD_ROWS[row][col + 1],
          BOARD_ROWS[row + 1][col],
          BOARD_ROWS[row + 1][col + 1]
        )
        corners.push({ topLeft, row, col })
      }
    }

    return corners
  }

  const splits = generateSplits()
  const corners = generateCorners()

  return (
    <div className="bg-green-900/90 p-3 md:p-4 rounded-xl border-4 border-amber-900 shadow-lg overflow-x-auto">
      <div className="min-w-fit">
        {/* Main grid */}
        <div className="flex gap-px relative">
          {/* Zero cell */}
          <div className="relative">
            <motion.button
              whileHover={!disabled ? { scale: 1.05 } : undefined}
              whileTap={!disabled ? { scale: 0.95 } : undefined}
              onClick={() => onNumberClick(0)}
              disabled={disabled}
              className={cn(
                'relative w-10 md:w-12 flex items-center justify-center',
                'font-bold text-white text-lg bg-green-700 hover:bg-green-600',
                'border border-casino-gold/30 rounded-l-lg',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              style={{ height: `${3 * 48 + 2}px` }}
            >
              0
              {getBetForPosition(bets, 'straight', 'straight-0') && (
                <BetChip amount={getBetForPosition(bets, 'straight', 'straight-0')!.amount} />
              )}
            </motion.button>

            {/* Splits with 0 */}
            {/* 0-3 split */}
            <motion.button
              whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 12px rgba(212, 175, 55, 0.6)' } : undefined}
              onClick={() => onSplitClick(0, 3)}
              disabled={disabled}
              className={cn(
                'absolute right-0 top-2 w-6 h-10 translate-x-1/2',
                'bg-casino-gold/10 hover:bg-casino-gold/40 rounded z-10',
                'border border-casino-gold/20 hover:border-casino-gold/60',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              title="Split 0-3"
            >
              {getBetForPosition(bets, 'split', 'split-0-3') && (
                <BetChip amount={getBetForPosition(bets, 'split', 'split-0-3')!.amount} />
              )}
            </motion.button>

            {/* 0-2 split */}
            <motion.button
              whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 12px rgba(212, 175, 55, 0.6)' } : undefined}
              onClick={() => onSplitClick(0, 2)}
              disabled={disabled}
              className={cn(
                'absolute right-0 top-1/2 w-6 h-10 translate-x-1/2 -translate-y-1/2',
                'bg-casino-gold/10 hover:bg-casino-gold/40 rounded z-10',
                'border border-casino-gold/20 hover:border-casino-gold/60',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              title="Split 0-2"
            >
              {getBetForPosition(bets, 'split', 'split-0-2') && (
                <BetChip amount={getBetForPosition(bets, 'split', 'split-0-2')!.amount} />
              )}
            </motion.button>

            {/* 0-1 split */}
            <motion.button
              whileHover={!disabled ? { scale: 1.3, zIndex: 40, boxShadow: '0 0 12px rgba(212, 175, 55, 0.6)' } : undefined}
              onClick={() => onSplitClick(0, 1)}
              disabled={disabled}
              className={cn(
                'absolute right-0 bottom-2 w-6 h-10 translate-x-1/2',
                'bg-casino-gold/10 hover:bg-casino-gold/40 rounded z-10',
                'border border-casino-gold/20 hover:border-casino-gold/60',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              title="Split 0-1"
            >
              {getBetForPosition(bets, 'split', 'split-0-1') && (
                <BetChip amount={getBetForPosition(bets, 'split', 'split-0-1')!.amount} />
              )}
            </motion.button>
          </div>

          {/* Number grid with overlays */}
          <div className="relative">
            {/* Number cells */}
            <div className="flex flex-col gap-px">
              {BOARD_ROWS.map((row, rowIndex) => (
                <div key={rowIndex} className="flex gap-px">
                  {row.map((num) => (
                    <NumberCell
                      key={num}
                      num={num}
                      bets={bets}
                      disabled={disabled}
                      onClick={() => onNumberClick(num)}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Horizontal splits overlay */}
            {splits
              .filter((s) => s.type === 'h')
              .map(({ num1, num2, row, col }) => (
                <div
                  key={`hsplit-${num1}-${num2}`}
                  className="absolute"
                  style={{
                    left: `${(col + 1) * (CELL_W_SM + 1)}px`,
                    top: `${row * (CELL_H_SM + 1) + CELL_H_SM / 2}px`,
                  }}
                >
                  <HorizontalSplit
                    num1={num1}
                    num2={num2}
                    bets={bets}
                    disabled={disabled}
                    onClick={() => onSplitClick(num1, num2)}
                  />
                </div>
              ))}

            {/* Vertical splits overlay */}
            {splits
              .filter((s) => s.type === 'v')
              .map(({ num1, num2, row, col }) => (
                <div
                  key={`vsplit-${num1}-${num2}`}
                  className="absolute"
                  style={{
                    left: `${col * (CELL_W_SM + 1) + CELL_W_SM / 2}px`,
                    top: `${(row + 1) * (CELL_H_SM + 1)}px`,
                  }}
                >
                  <VerticalSplit
                    num1={num1}
                    num2={num2}
                    bets={bets}
                    disabled={disabled}
                    onClick={() => onSplitClick(num1, num2)}
                  />
                </div>
              ))}

            {/* Corner bets overlay */}
            {corners.map(({ topLeft, row, col }) => (
              <div
                key={`corner-${topLeft}`}
                className="absolute"
                style={{
                  left: `${(col + 1) * (CELL_W_SM + 1)}px`,
                  top: `${(row + 1) * (CELL_H_SM + 1)}px`,
                }}
              >
                <CornerBet
                  topLeft={topLeft}
                  bets={bets}
                  disabled={disabled}
                  onClick={() => onCornerClick(topLeft)}
                />
              </div>
            ))}

            {/* Street bets (left edge of first column for each row of 3) */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((streetNum) => {
              const col = streetNum - 1
              return (
                <div
                  key={`street-${streetNum}`}
                  className="absolute"
                  style={{
                    left: `${col * (CELL_W_SM + 1)}px`,
                    top: `${2 * (CELL_H_SM + 1) + CELL_H_SM + 2}px`,
                  }}
                >
                  <StreetBet
                    row={streetNum}
                    bets={bets}
                    disabled={disabled}
                    onClick={() => onStreetClick(streetNum)}
                  />
                </div>
              )
            })}

            {/* Line bets (between adjacent streets) */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((lineNum) => {
              const col = lineNum - 1
              return (
                <div
                  key={`line-${lineNum}`}
                  className="absolute"
                  style={{
                    left: `${(col + 1) * (CELL_W_SM + 1) - 2}px`,
                    top: `${2 * (CELL_H_SM + 1) + CELL_H_SM + 2}px`,
                  }}
                >
                  <LineBet
                    row={lineNum}
                    bets={bets}
                    disabled={disabled}
                    onClick={() => onLineClick(lineNum)}
                  />
                </div>
              )
            })}
          </div>

          {/* Column bets (2 to 1) */}
          <div className="flex flex-col gap-px ml-px">
            {[3, 2, 1].map((col) => {
              const columnBet = bets.find((b) => b.type === 'column' && b.position === `column-${col}`)
              return (
                <motion.button
                  key={col}
                  whileHover={!disabled ? { scale: 1.02 } : undefined}
                  whileTap={!disabled ? { scale: 0.98 } : undefined}
                  onClick={() => onColumnClick(col as 1 | 2 | 3)}
                  disabled={disabled}
                  className={cn(
                    'relative w-10 md:w-12 h-12 md:h-14 flex items-center justify-center',
                    'font-bold text-white text-xs md:text-sm',
                    'border border-casino-gold/30 transition-all',
                    'bg-casino-black-light hover:bg-casino-black',
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && 'cursor-pointer'
                  )}
                >
                  2:1
                  {columnBet && (
                    <div className="absolute -top-1 -right-1 z-20">
                      <BetChip amount={columnBet.amount} />
                    </div>
                  )}
                </motion.button>
              )
            })}
          </div>
        </div>

        {/* Dozen bets */}
        <div className="flex gap-px mt-px ml-10 md:ml-12">
          {[
            { dozen: 1 as const, label: '1-12' },
            { dozen: 2 as const, label: '13-24' },
            { dozen: 3 as const, label: '25-36' },
          ].map(({ dozen, label }) => {
            const dozenBet = bets.find((b) => b.type === 'dozen' && b.position === `dozen-${dozen}`)
            return (
              <motion.button
                key={dozen}
                whileHover={!disabled ? { scale: 1.02 } : undefined}
                whileTap={!disabled ? { scale: 0.98 } : undefined}
                onClick={() => onDozenClick(dozen)}
                disabled={disabled}
                className={cn(
                  'relative flex-1 h-10 md:h-12 flex items-center justify-center',
                  'font-bold text-white text-xs md:text-sm',
                  'border border-casino-gold/30 transition-all',
                  'bg-casino-black-light hover:bg-casino-black',
                  disabled && 'opacity-50 cursor-not-allowed',
                  !disabled && 'cursor-pointer'
                )}
              >
                {label}
                {dozenBet && (
                  <div className="absolute -top-1 -right-1 z-20">
                    <BetChip amount={dozenBet.amount} />
                  </div>
                )}
              </motion.button>
            )
          })}
        </div>

        {/* Outside bets */}
        <div className="flex gap-px mt-px ml-10 md:ml-12">
          <OutsideBetCell
            label="1-18"
            betType="low"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('low')}
            className="flex-1 h-10 md:h-12"
          />
          <OutsideBetCell
            label="PAR"
            betType="even"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('even')}
            className="flex-1 h-10 md:h-12"
          />
          <OutsideBetCell
            label="◆"
            betType="red"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('red')}
            className="flex-1 h-10 md:h-12"
            color="red"
          />
          <OutsideBetCell
            label="◆"
            betType="black"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('black')}
            className="flex-1 h-10 md:h-12"
            color="black"
          />
          <OutsideBetCell
            label="IMPAR"
            betType="odd"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('odd')}
            className="flex-1 h-10 md:h-12"
          />
          <OutsideBetCell
            label="19-36"
            betType="high"
            bets={bets}
            disabled={disabled}
            onClick={() => onOutsideClick('high')}
            className="flex-1 h-10 md:h-12"
          />
        </div>
      </div>
    </div>
  )
}
